import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lightbulb, Beaker, Rocket, Factory, Loader2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => n >= 1_000_000
  ? `${(n / 1_000_000).toFixed(1)}M €`
  : n >= 1000 ? `${(n / 1000).toFixed(0)}K €` : `${Math.round(n)} €`;

const STAGE_META = {
  concept: { icon: Lightbulb, color: "#8b5cf6", label: "Concepto" },
  pilot: { icon: Beaker, color: "#f59e0b", label: "Piloto" },
  ready_for_market: { icon: Rocket, color: "#10b981", label: "Listo para escalar" },
  in_production: { icon: Factory, color: "#3b82f6", label: "Producción" },
};

/**
 * InnovationCycleFunnel — funnel horizontal Concept → Piloto → Ready → Producción
 *
 * Prop `variant`:
 *  - "compact" (default): tarjetas horizontales pequeñas, ideal para Inicio.
 *  - "full": layout expandido con ejemplos por etapa, para /impacto.
 */
export default function InnovationCycleFunnel({ variant = "compact" }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/analytics/innovation-cycle`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok && alive) setData(await r.json());
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-border p-4 bg-card flex items-center gap-3" data-testid="innovation-cycle-loading">
        <Loader2 className="w-4 h-4 animate-spin text-emerald-600"/>
        <span className="text-xs text-muted-foreground">Cargando ciclo de innovación…</span>
      </div>
    );
  }
  if (!data) return null;

  const stages = data.stages || [];
  const conversions = data.conversions || [];
  const isCompact = variant === "compact";

  const stageClick = (key) => {
    if (key === "concept") navigate("/portfolio");
    else if (key === "pilot") navigate("/pilotos");
    else navigate("/app-catalog");
  };

  return (
    <div className={isCompact ? "rounded-2xl border border-border bg-card p-4" : "space-y-4"} data-testid="innovation-cycle-funnel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Ciclo de Innovación</div>
          {!isCompact && <h2 className="text-xl font-black tracking-tight">Concepto → Piloto → Listo para escalar → Producción</h2>}
          {isCompact && <div className="text-sm font-bold">Concepto → Producción</div>}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase text-muted-foreground">Inversión total</div>
          <div className="text-base font-black text-emerald-700">{eur(data.total_investment_eur || 0)}</div>
        </div>
      </div>

      <div className={isCompact ? "flex items-stretch gap-1 overflow-x-auto" : "grid grid-cols-1 md:grid-cols-4 gap-3"}>
        {stages.map((s, i) => {
          const meta = STAGE_META[s.key] || { icon: Lightbulb, color: "#666", label: s.label };
          const Icon = meta.icon;
          const conv = conversions[i];
          return (
            <div key={s.key} className="flex items-stretch gap-1 flex-1 min-w-0">
              <motion.button
                layout
                onClick={() => stageClick(s.key)}
                whileHover={{ y: -2 }}
                className={`flex-1 min-w-0 text-left rounded-xl border p-3 transition-all hover:shadow-md ${isCompact ? "" : "space-y-2"}`}
                style={{ borderColor: `${meta.color}44`, background: `${meta.color}0d` }}
                data-testid={`cycle-stage-${s.key}`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon className="w-4 h-4" style={{ color: meta.color }}/>
                  <span className="text-[10px] uppercase font-bold tracking-wider" style={{ color: meta.color }}>{s.label}</span>
                </div>
                <div className="text-2xl font-black leading-none" style={{ color: meta.color }}>{s.count}</div>
                {!isCompact && (
                  <div className="text-[11px] text-muted-foreground line-clamp-2">{s.description}</div>
                )}
                {(s.cost_total || 0) > 0 && (
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Inversión: <strong>{eur(s.cost_total)}</strong>
                    {s.count > 0 && <> · media {eur(s.cost_avg)}</>}
                  </div>
                )}
                {!isCompact && s.items?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border space-y-0.5">
                    {s.items.slice(0, 4).map((it) => (
                      <div key={it.id} className="text-[10px] text-muted-foreground truncate">
                        · {it.name}
                      </div>
                    ))}
                    {s.total_items > 4 && (
                      <div className="text-[10px] text-muted-foreground/60 italic">… y {s.total_items - 4} más</div>
                    )}
                  </div>
                )}
              </motion.button>
              {i < stages.length - 1 && conversions[i + 1] && (
                <div className="flex flex-col items-center justify-center px-1 flex-shrink-0" title={`Conversión: ${conversions[i + 1].pct !== null ? conversions[i + 1].pct + "%" : "—"}`}>
                  <ArrowRight className="w-3 h-3 text-muted-foreground/60"/>
                  <div className="text-[9px] text-muted-foreground/70 font-bold">
                    {conversions[i + 1].pct !== null ? `${conversions[i + 1].pct}%` : "—"}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!isCompact && data.universe_note && (
        <p className="text-[11px] text-muted-foreground italic" data-testid="innovation-cycle-universe-note">
          {data.universe_note}
        </p>
      )}

      {isCompact && (
        <button
          onClick={() => navigate("/impacto?tab=cycle")}
          className="w-full mt-3 text-[10px] font-bold text-emerald-700 hover:text-emerald-900 uppercase tracking-wide"
          data-testid="innovation-cycle-open-full"
        >
          Ver desglose completo →
        </button>
      )}
    </div>
  );
}
